import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ShareIcon } from '@heroicons/react/24/outline';
import { apiService, Product as ApiProduct } from '../services/api';
import LogoLoader from '../components/ui/LogoLoader';
import { useCart } from '../context/CartContext';
import { facebookPixel } from '../utils/facebookPixel';

// Use API interfaces directly
type Product = ApiProduct & {
  rating?: number;
  reviews?: number;
  discount?: number;
  material?: string;
  origin?: string;
};

const ProductDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [showColorWarning, setShowColorWarning] = useState(false);
  const [showSizeWarning, setShowSizeWarning] = useState(false);

  // Memoized computed values - must be before any early returns
  const availableColors = useMemo(() => {
    if (!product?.variants) return [];
    const colorMap = new Map();
    product.variants.forEach(v => {
      if (!colorMap.has(v.color)) {
        colorMap.set(v.color, { 
          color: v.color, 
          color_code: v.color_code || '#000000' 
        });
      }
    });
    return Array.from(colorMap.values());
  }, [product?.variants]);

  const availableSizes = useMemo(() => {
    if (!product?.variants || !selectedColor) return [];
    // Get all unique sizes for the selected color from the sizes array
    const sizesForColor: string[] = [];
    product.variants
      .filter(v => v.color === selectedColor)
      .forEach(v => {
        v.sizes.forEach(size => {
          if (size.name && !sizesForColor.includes(size.name)) {
            sizesForColor.push(size.name);
          }
        });
      });
    return sizesForColor;
  }, [product?.variants, selectedColor]);

  // Fetch product data from API
  useEffect(() => {
    const fetchProduct = async () => {
      if (!slug) return;
      
      try {
        setLoading(true);
        setError(null);
        const product = await apiService.getProductBySlug(slug);
        setProduct(product);
        
        // Track Facebook Pixel ViewContent event
        facebookPixel.trackViewContent({
          content_name: product.name,
          content_category: typeof product.category === 'string' ? product.category : product.category?.name,
          content_ids: [product.id.toString()],
          content_type: 'product',
          value: product.price,
          currency: 'MMK'
        });
        
        // Don't auto-select color and size - let user choose
        // This ensures users must make their own selection
      } catch (error: any) {
        setError(error.response?.data?.message || 'Product not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [slug]);

  // Auto-select first available size when color changes
  useEffect(() => {
    if (selectedColor && availableSizes.length > 0 && !selectedSize) {
      setSelectedSize(availableSizes[0]);
    }
  }, [selectedColor, availableSizes, selectedSize]);

  // Reset quantity when size changes and current quantity exceeds available stock
  useEffect(() => {
    if (product && selectedColor && selectedSize) {
      const currentVariant = product.variants?.find(v => 
        v.color === selectedColor && v.is_active
      );
      const currentSize = currentVariant?.sizes?.find(s => 
        s.name === selectedSize && s.is_active
      );
      // Calculate available stock (total stock minus reserved stock)
      const totalStock = currentSize?.stock_quantity || 0;
      const reservedStock = currentSize?.reserved_quantity || 0;
      const availableStock = Math.max(0, totalStock - reservedStock);
      
      if (availableStock > 0 && quantity > availableStock) {
        setQuantity(availableStock);
      }
    }
  }, [product, selectedSize, selectedColor, quantity]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LogoLoader size="lg" text="Loading product..." />
      </div>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Product not found'}</p>
          <Link 
            to="/product-signature" 
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  // Helper functions
  const getProductImages = () => {
    return product.product_images && product.product_images.length > 0 
      ? product.product_images 
      : ['https://images.unsplash.com/photo-1602810318383-e386cc2a3ccf?w=600&h=600&fit=crop'];
  };

  const getDiscountPercentage = () => {
    if (product.compare_at_price && product.price < product.compare_at_price) {
      return Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100);
    }
    return 0;
  };



  const getCategoryName = () => {
    if (typeof product.category === 'object' && product.category) {
      return product.category.name;
    }
    if (typeof product.category === 'string') {
      return product.category;
    }
    return 'Uncategorized';
  };



  // Get current variant and size based on selections
  const currentVariant = product.variants?.find(v => 
    v.color === selectedColor && v.is_active
  );
  
  const currentSize = currentVariant?.sizes?.find(s => 
    s.name === selectedSize && s.is_active
  );

  const currentPrice = currentSize?.price || currentVariant?.price || product.price;
  const comparePrice = currentSize?.compare_at_price || currentVariant?.compare_at_price || product.compare_at_price;
  // Calculate available stock (total stock minus reserved stock)
  const totalStock = currentSize?.stock_quantity || 0;
  const reservedStock = currentSize?.reserved_quantity || 0;
  const stockQuantity = Math.max(0, totalStock - reservedStock);
  const isInStock = stockQuantity > 0;

  const images = getProductImages();
  const discountPercentage = getDiscountPercentage();
  const categoryName = getCategoryName();

  // Promotion handling — use backend-computed promotional_price first, then active_promotions
  const activePromotion: any = (product as any)?.active_promotions && (product as any).active_promotions.length > 0
    ? (product as any).active_promotions[0]
    : null;

  const computePromotionalPrice = (basePrice: number, promotion: any) => {
    // 1. Use saved promotional_price from backend if it's less than base
    const saved = (product as any)?.promotional_price;
    if (saved && saved > 0 && saved < basePrice) return saved;

    // 2. Fall back to computing from active_promotions
    if (!promotion || !basePrice || basePrice <= 0) return undefined;
    const type = promotion.type;
    const value = promotion.value;
    if (type === 'percentage') {
      const rawDiscount = (basePrice * (value / 100));
      const maxCap = promotion.max_discount_amount || promotion.max_discount || null;
      const appliedDiscount = maxCap ? Math.min(rawDiscount, maxCap) : rawDiscount;
      const discounted = basePrice - appliedDiscount;
      return Math.max(0, Number(discounted.toFixed(2)));
    }
    if (type === 'fixed' || type === 'fixed_amount') {
      const discounted = basePrice - value;
      return Math.max(0, Number(discounted.toFixed(2)));
    }
    return undefined;
  };

  const promotionalPrice = computePromotionalPrice(currentPrice || 0, activePromotion);
  const promoDiscountPercent = promotionalPrice !== undefined && currentPrice
    ? Math.round(((currentPrice - promotionalPrice) / currentPrice) * 100)
    : 0;

  // Compute cart pricing and opts
  const unitPriceForCart = (promotionalPrice ?? currentPrice) as number;
  const compareForCart = (() => {
    if (promotionalPrice !== undefined && currentPrice) return currentPrice;
    if (comparePrice && comparePrice > (currentPrice || 0)) return comparePrice;
    return undefined;
  })();

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!product) return;
    
    // Reset warnings
    setShowColorWarning(false);
    setShowSizeWarning(false);
    
    // Check if product has variants and user hasn't selected size/color
    if ((product.variants?.length ?? 0) > 0) {
      let hasError = false;
      
      if (!selectedColor) {
        setShowColorWarning(true);
        hasError = true;
      }
      if (!selectedSize) {
        setShowSizeWarning(true);
        hasError = true;
      }
      
      if (hasError) {
        return;
      }
    }
    
    addToCart(product, quantity, {
      variantId: currentVariant?.id as any,
      sizeName: selectedSize || undefined,
      unitPrice: unitPriceForCart,
      compareAtPrice: compareForCart,
      inStock: isInStock,
      colorName: selectedColor || (currentVariant as any)?.color,
      colorCode: (currentVariant as any)?.color_code,
    });
    // button feedback
    const btn = e.currentTarget;
    btn.classList.add('animate-bounce');
    setTimeout(() => btn.classList.remove('animate-bounce'), 500);
  };

  const handleShopNow = () => {
    if (!product) return;
    
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      // Redirect to login
      window.location.href = '/login';
      return;
    }

    // Reset warnings
    setShowColorWarning(false);
    setShowSizeWarning(false);

    // Check if product has variants and user hasn't selected size/color
    if ((product.variants?.length ?? 0) > 0) {
      let hasError = false;
      
      if (!selectedColor) {
        setShowColorWarning(true);
        hasError = true;
      }
      if (!selectedSize) {
        setShowSizeWarning(true);
        hasError = true;
      }
      
      if (hasError) {
        return;
      }
    }
    
    // Add to cart
    addToCart(product, quantity, {
      variantId: currentVariant?.id as any,
      sizeName: selectedSize || undefined,
      unitPrice: unitPriceForCart,
      compareAtPrice: compareForCart,
      inStock: isInStock,
      colorName: selectedColor || (currentVariant as any)?.color,
      colorCode: (currentVariant as any)?.color_code,
    });
    
    // Navigate to checkout
    navigate('/checkout');
  };

  const handleShare = async () => {
    if (!product) return;

    const shareData = {
      title: product.name,
      text: `Check out this amazing product: ${product.name}`,
      url: window.location.href,
    };

    try {
      // Check if Web Share API is supported
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: Copy URL to clipboard
        await navigator.clipboard.writeText(window.location.href);
        
        // Show a temporary notification
        const notification = document.createElement('div');
        notification.textContent = 'Product link copied to clipboard!';
        notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
        document.body.appendChild(notification);
        
        // Remove notification after 3 seconds
        setTimeout(() => {
          notification.style.opacity = '0';
          setTimeout(() => {
            document.body.removeChild(notification);
          }, 300);
        }, 3000);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      
      // Fallback: Try to copy URL if sharing fails
      try {
        await navigator.clipboard.writeText(window.location.href);
        
        // Show a temporary notification
        const notification = document.createElement('div');
        notification.textContent = 'Product link copied to clipboard!';
        notification.className = 'fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-md shadow-lg z-50 transition-opacity duration-300';
        document.body.appendChild(notification);
        
        // Remove notification after 3 seconds
        setTimeout(() => {
          notification.style.opacity = '0';
          setTimeout(() => {
            document.body.removeChild(notification);
          }, 300);
        }, 3000);
      } catch (clipboardError) {
        console.error('Error copying to clipboard:', clipboardError);
        alert('Unable to share. Please copy the URL manually.');
      }
    }
  };

  // Format price helper
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US').format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-[41px]">
      {/* Breadcrumb */}
      <div className="border-b border-gray-200" style={{ backgroundColor: 'rgb(247, 241, 240)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-8 text-center">
            <nav
              className="flex items-center justify-center space-x-2 text-sm"
              aria-label="Breadcrumb"
            >
              <Link 
                to="/" 
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                Home
              </Link>
              <span className="text-gray-400">/</span>
              <Link 
                to="/product-signature" 
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                Products
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-gray-600">{product.name}</span>
            </nav>
          </div>
        </div>
      </div>

      {/* Product Detail */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Product Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={images[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Promotion Label */}
              {activePromotion && promotionalPrice !== undefined && promotionalPrice < (currentPrice || 0) && (
                <div className="text-sm text-green-700 bg-green-50 inline-flex items-center px-2 py-1 rounded-md">
                  <span className="font-medium mr-1">Promotion:</span>
                  <span>
                    {activePromotion.name} (
                    {activePromotion.type === 'percentage'
                      ? `${activePromotion.value}% off`
                      : `-${formatPrice(activePromotion.value)} MMK`}
                    )
                  </span>
                </div>
              )}
              
              {/* Thumbnail Images */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {images.map((image: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`aspect-square bg-gray-100 rounded-md overflow-hidden border-2 transition-colors ${
                        selectedImage === index ? 'border-gray-900' : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${product.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-4">
              {/* Product Title */}
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h1>
                <p className="hidden text-sm text-gray-600">{categoryName}</p>
              </div>

              {/* Price */}
              <div className="flex items-center space-x-3">
                <span className="text-2xl font-bold text-gray-900">
                  {formatPrice((promotionalPrice ?? currentPrice) as number)} MMK
                </span>
                {promotionalPrice !== undefined && promotionalPrice < (currentPrice || 0) ? (
                  <>
                    <span className="text-lg text-gray-500 line-through">
                      {formatPrice(currentPrice as number)} MMK
                    </span>
                    {promoDiscountPercent > 0 && (
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                        -{promoDiscountPercent}%
                      </span>
                    )}
                  </>
                ) : (
                  comparePrice && comparePrice > currentPrice && (
                    <>
                      <span className="text-lg text-gray-500 line-through">
                        {formatPrice(comparePrice)} MMK
                      </span>
                      {discountPercentage && discountPercentage > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                          -{discountPercentage}%
                        </span>
                      )}
                    </>
                  )
                )}
              </div>

              {/* Description */}
              {product.description && (
                <div>
                  <p className="text-gray-700 text-sm leading-relaxed">{product.description}</p>
                </div>
              )}

              {/* Features */}
              {product.features && product.features.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Features</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <span className="mr-2 mt-1 flex-shrink-0 h-2 w-2 rounded-full border border-gray-900 inline-block"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Color Selection */}
              {availableColors.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Color</h3>
                  <div className="flex items-center space-x-2">
                    {availableColors.map((colorOption) => (
                      <button
                        key={colorOption.color}
                        onClick={() => {
                          setSelectedColor(colorOption.color);
                          setSelectedSize(''); // Reset size when color changes - let user choose
                          setShowColorWarning(false); // Hide warning when color is selected
                        }}
                        className={`w-8 h-8 rounded-full border-2 transition-all ${
                          selectedColor === colorOption.color
                            ? 'border-gray-900 scale-110'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        style={{ backgroundColor: colorOption.color_code }}
                        title={colorOption.color}
                      />
                    ))}
                    {selectedColor && (
                      <span className="text-sm text-gray-600 ml-2">{selectedColor}</span>
                    )}
                  </div>
                  {showColorWarning && (
                    <p className="text-red-500 text-sm mt-1 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Please select a color
                    </p>
                  )}
                </div>
              )}

              {/* Size Selection */}
              {availableSizes.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Size</h3>
                  <div className="flex items-center space-x-2 flex-wrap">
                    {availableSizes.map((size) => {
                      const colorVariant = product.variants?.find(v => 
                        v.color === selectedColor && v.is_active
                      );
                      const sizeData = colorVariant?.sizes?.find(s => 
                        s.name === size && s.is_active
                      );
                      // Calculate available stock (total stock minus reserved stock)
                      const totalStock = sizeData?.stock_quantity || 0;
                      const reservedStock = sizeData?.reserved_quantity || 0;
                      const sizeStock = Math.max(0, totalStock - reservedStock);
                      const isOutOfStock = sizeStock <= 0;
                      
                      return (
                        <button
                          key={size}
                          onClick={() => {
                            if (!isOutOfStock) {
                              setSelectedSize(size);
                              setShowSizeWarning(false); // Hide warning when size is selected
                            }
                          }}
                          disabled={isOutOfStock}
                          className={`relative px-3 py-2 text-sm border rounded-md transition-colors ${
                            selectedSize === size
                              ? 'border-gray-900 bg-gray-900 text-white'
                              : isOutOfStock
                              ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                              : 'border-gray-300 hover:border-gray-400'
                          }`}
                        >
                          {size}
                          {isOutOfStock && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-full h-0.5 bg-gray-400 transform rotate-45"></div>
                            </div>
                          )}
                          {!isOutOfStock && sizeStock <= 5 && (
                            <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                              {sizeStock}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {showSizeWarning && (
                    <p className="text-red-500 text-sm mt-1 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Please select a size
                    </p>
                  )}
                </div>
              )}

              {/* Quantity */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-2">Quantity</h3>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center border border-gray-300 rounded-md">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                      className={`px-3 py-1.5 rounded-l-md transition-colors ${
                        quantity <= 1
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    >
                      −
                    </button>
                    <span className="px-4 py-1.5 border-x border-gray-300 bg-gray-50 font-medium text-sm">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(Math.min(stockQuantity, quantity + 1))}
                      disabled={quantity >= stockQuantity}
                      className={`px-3 py-1.5 rounded-r-md transition-colors ${
                        quantity >= stockQuantity
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2 pt-3">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleAddToCart}
                    className="bg-gray-900 text-white py-2.5 px-4 rounded-md font-semibold hover:bg-gray-800 transition-colors text-sm"
                    disabled={!!(selectedSize && !isInStock)}
                  >
                    {selectedSize && !isInStock ? 'OUT OF STOCK' : 'ADD TO CART'}
                  </button>
                  
                  <button
                    onClick={handleShopNow}
                    className="bg-gray-700 text-white py-2.5 px-4 rounded-md font-semibold hover:bg-gray-600 transition-colors text-sm"
                    disabled={!!(selectedSize && !isInStock)}
                  >
                    {selectedSize && !isInStock ? 'OUT OF STOCK' : 'BUY NOW'}
                  </button>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <button 
                    onClick={handleShare}
                    className="flex items-center justify-center py-2 px-3 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm"
                  >
                    <ShareIcon className="h-4 w-4 text-gray-400 mr-1" />
                    Share
                  </button>
                </div>
              </div>


            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
